// phone-validator.ts

import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';

/**
 * Validation error keys for phoneValidator.
 */
export interface PhoneValidationErrors extends ValidationErrors {
  required?: true;
  countryNotAllowed?: true;
  invalidFormat?: true;
  notPossible?: true;
  notMobile?: true;
  tooShort?: { minLength: number };
}

/**
 * Creates a ValidatorFn that checks international phone numbers:
 *  - Required
 *  - Country in allowed list
 *  - Correct E.164 format
 *  - Possible number per libphonenumber-js
 *  - Must be MOBILE
 *  - Minimum national number length per country
 *
 * @param countryIsoSelector - function returning the currently selected country ISO
 */
export function phoneValidator(
  countryIsoSelector: () => TuiCountryIsoCode
): ValidatorFn {
  // Whitelisted countries
  const allowed: TuiCountryIsoCode[] = [
    TuiCountryIsoCode.ES,
    TuiCountryIsoCode.GB,
    TuiCountryIsoCode.FR,
    TuiCountryIsoCode.IT,
    TuiCountryIsoCode.DE,
    TuiCountryIsoCode.PT,
    TuiCountryIsoCode.NL,
    TuiCountryIsoCode.AD,
    TuiCountryIsoCode.CL,
    TuiCountryIsoCode.DO,
  ];

  // Minimum national number length per country (defaults to 9)
  const minLenByCountry: Partial<Record<TuiCountryIsoCode, number>> = {
    [TuiCountryIsoCode.DE]: 10,
    [TuiCountryIsoCode.AD]: 6,
    [TuiCountryIsoCode.ES]: 9,
    [TuiCountryIsoCode.GB]: 10,
    [TuiCountryIsoCode.FR]: 9,
    [TuiCountryIsoCode.IT]: 10,
    [TuiCountryIsoCode.PT]: 9,
    [TuiCountryIsoCode.NL]: 9,
    [TuiCountryIsoCode.CL]: 9,
    [TuiCountryIsoCode.DO]: 10,
  };

  return (control: AbstractControl): PhoneValidationErrors | null => {
    const raw: string = (control.value || '').toString().trim();

    // 1) Required
    if (!raw) {
      return null;
    }

    // 2) Country allowed?
    const iso = countryIsoSelector();
    if (!allowed.includes(iso)) {
      return { countryNotAllowed: true };
    }

    // 2.5) Clean formatting characters
    const cleaned = raw.replace(/[^\d+]/g, '');

    let phone: PhoneNumber | undefined;
    try {
      // 3) Parse + validate E.164
      phone = parsePhoneNumberFromString(cleaned, iso);
      if (!phone) {
        return { invalidFormat: true };
      }
    } catch (e) {
      return { invalidFormat: true };
    }

    // 4) Is a possible number?
    if (!phone.isPossible()) {
      return { invalidFormat: true };
    }

    // 5) Is valid number?
    if (!phone.isValid()) {
      return { invalidFormat: true };
    }

    // 6) Minimum length per country
    const national = phone.nationalNumber;
    const minLength = minLenByCountry[iso] ?? 9;

    if (national.length < minLength) {
      return { tooShort: { minLength } };
    }

    return null;
  };
}
